import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * 5a · Book Konfigurator state — text only, per Sebastian ruling
 * 2026-08-06 (Option 3 after the photo-persistence conflict).
 *
 * Block 2 commit 1 (Sebastian ruling 2026-08-31): reshaped for a
 * flat cast list without a Buchheld/Hauptperson distinction. Every
 * cast entry now carries `personType` (Person/Tier, default 'person'
 * so the common case needs no interaction), `geschlecht` (männlich/
 * weiblich/keineAngabe — asked for both Person and Tier because the
 * story text needs a pronoun), and optional `personenNotiz` (free
 * text for details the pipeline cannot see in the photo — kann noch
 * nicht laufen / trägt eine Brille / hat einen Gipsarm; 200-char
 * cap). The `isProtagonist` field is DELETED — Block 3 Mittelpunkt
 * is gone; language derivation uses the oldest cast member under 18
 * per protagonist-resolver's new two-case rule. Added at book level:
 * optional `titelwunsch` (40-char cap) collected in its own step
 * after the theme input; seeds book_orders.story_text.title at
 * creation so the anchor-title service short-circuits.
 *
 * ═══ WHAT THIS STORE HOLDS ═════════════════════════════════════════
 *
 * Text state only: the customer's cast entries + Freitext + Sprache
 * + Titelwunsch + the reload-trap `blockNav` + the Geschichtenschritt
 * prototype's storyKind/erfundenTile/kind-buckets. Persisted to
 * localStorage under `dreamable-book-configurator` so a closed tab
 * does NOT lose what the customer typed.
 *
 * ═══ WHAT THIS STORE DOES NOT HOLD ═════════════════════════════════
 *
 * Photos. This store carries TEXT ONLY.
 *
 * Photos live in IndexedDB via `../state/photo-picks-idb.ts` — File
 * objects survive tab reload through structured clone, keyed by
 * `castKey`. `PersonenRoute` hydrates on mount and writes through on
 * every pick / remove; `LightGateOverlay` clears the whole IDB store
 * on Light-Gate success (photos are on the server, IDB is redundant
 * AND carries children's photos as PII).
 *
 * The prior transient-only ruling (2026-08-06, tracker
 * `[book-photos-no-preauth-upload]`) is SUPERSEDED by Sebastian ruling
 * 2026-08-12 (Befund 5). Reason for the reversal: the reload gap kept
 * trapping customers mid-flow and the "just re-pick" workaround had
 * to close.
 *
 * ═══ CAST-KEY GENERATION ═══════════════════════════════════════════
 *
 * Each cast entry carries a locally-generated `key` (short UUID
 * fragment). Cast entries are equal — cast[0] is no longer special
 * (pre-Block-2 it was the Hauptperson). Order matters only for the
 * DTO payload (backend persists intake.cast[] in order) and
 * UI display.
 */

/**
 * Block 2 commit 1 (Sebastian ruling 2026-08-31) — Gattung values.
 * "Person" / "Tier". Kuscheltier is P-97 and NOT in this commit.
 */
export type CastPersonType = 'person' | 'tier';

/**
 * Block 2 commit 1 (Sebastian ruling 2026-08-31) — Geschlecht values.
 * Three options exactly. Asked for BOTH Person and Tier per Sebastian:
 * a dog is a Rüde or a Hündin and the story has to write "er" or "sie".
 *
 * Sebastian ruling 2026-09-19 — the empty-string sentinel `''` is
 * added for the "unset" state on person entries. A new person entry
 * starts with `geschlecht: ''` (no button pressed); the customer must
 * pick one of the three real values before Save enables. Picking
 * `'keineAngabe'` is a valid choice and enables Save. On animal
 * entries the default `'keineAngabe'` is retained — sex is optional
 * on the Tier branch and never gates saving.
 *
 * Persisted store rows from before this ruling carry
 * `geschlecht: 'keineAngabe'` and continue to load without
 * migration — the empty-string sentinel is a fresh-mount default
 * only, never persisted for a saved entry.
 */
export type CastGeschlecht = '' | 'maennlich' | 'weiblich' | 'keineAngabe';

/**
 * Block 2 commit 1 (Sebastian ruling 2026-08-31) — length cap on
 * per-person free text.
 *
 * fix/text-field-limits (Sebastian ruling 2026-09-29) — authoring
 * moved to `constants/text-limits.ts` (single PWA point of truth).
 * Re-exported here to avoid churn on every existing PersonenFlow
 * import; new consumers should import from `text-limits.ts`
 * directly.
 */
export { CREATE_BOOK_ORDER_PERSONEN_NOTIZ_MAX_LENGTH as CAST_PERSONEN_NOTIZ_MAX_LENGTH } from '../constants/text-limits';

/**
 * Block 2 commit 1 (Sebastian ruling 2026-08-31) — length cap on
 * book-level title wish.
 *
 * fix/text-field-limits (Sebastian ruling 2026-09-29) — authoring
 * moved to `constants/text-limits.ts`. Re-exported here to avoid
 * churn on existing consumers.
 */
export { CREATE_BOOK_ORDER_TITELWUNSCH_MAX_LENGTH as TITELWUNSCH_MAX_LENGTH } from '../constants/text-limits';

/**
 * Sebastian ruling 2026-09-15 — UI cap on the "Was soll anders sein?"
 * textarea on the summary-review screen (SummaryReviewScreen.tsx).
 *
 * A round-number stop displayed to the customer with a live counter.
 * Deliberately LESS than the backend DTO cap so a submission that
 * passes this stop still passes the DTO:
 *
 *   backend DTOs (retry + self-write) enforce
 *     `SUMMARY_TEXT_MAX_LENGTH` = 2000 (standalone since 2026-09-29;
 *     see `src/book/summary-review/dto/self-write.dto.ts` for the
 *     story-model-input rationale)
 *   this UI cap                                       = 1000
 *   headroom                                          = 1000
 *
 * Invariant protected by a regression test in
 * `src/book/summary-review/dto/retry.dto.spec.ts` — if the DTO cap
 * ever drops below 1000, the test fails and this constant must move
 * too. Without that gate the UI would tell a customer their text is
 * fine while the DTO rejects it — the failure mode we designed the
 * counter to prevent.
 */
export const SUMMARY_CUSTOMER_CHANGE_MAX_LENGTH = 1000;

/**
 * Position-2 Schritt B (Sebastian ruling 2026-09-11) — length cap on
 * the concrete-species field.
 *
 * fix/text-field-limits (Sebastian ruling 2026-09-29) — authoring
 * moved to `constants/text-limits.ts`. Re-exported here to avoid
 * churn on existing consumers.
 */
export { CREATE_BOOK_ORDER_SPECIES_MAX_LENGTH as CAST_SPECIES_MAX_LENGTH } from '../constants/text-limits';

/**
 * Sebastian ruling 2026-09-18 — PWA-side mirror of the backend
 * `BOOK_CONFIG_CONSTANTS.max_cast_entries` (`src/book/config/book-config.constants.ts`).
 * Enforced backend-side by `@ArrayMaxSize(BOOK_CONFIG_CONSTANTS.max_cast_entries)`
 * on `CreateBookOrderDto.cast`. Consumed here so `PersonenRoute.tsx`
 * can gate the "add another person" affordance and render the cap-
 * reached note at the correct number.
 */
export const CAST_MAX_ENTRIES = 9;

export interface CastTextEntry {
  readonly key: string;
  readonly name: string;
  /** Integer year. `null` while empty. Range 0..120 (backend `C-12`). */
  readonly ageYears: number | null;
  /**
   * Block 2 commit 1 — Gattung. Defaults to 'person' at add-time so
   * the common case needs no interaction (Sebastian ruling: "Prefilled
   * with 'Person' so the normal case needs no interaction").
   */
  readonly personType: CastPersonType;
  /**
   * Block 2 commit 1 — Geschlecht. Defaults to 'keineAngabe' at
   * add-time. Reaches the story generation via story.processor.ts's
   * `composeCastDescription` — the register entry names that pronoun
   * correctness on ambiguous names is not guaranteed without a rule
   * change in the frozen story artefact.
   */
  readonly geschlecht: CastGeschlecht;
  /**
   * Block 2 commit 1 — optional per-person free text. Empty string
   * when the customer wrote nothing.
   */
  readonly personenNotiz: string;
  /**
   * Position-2 Schritt B (Sebastian ruling 2026-09-11) — concrete
   * animal noun (Hund, Katze, Pony, ...) when personType === 'tier'.
   * Empty string default so the shape is stable across all rows;
   * PersonenRoute only renders the input on tier entries and the
   * "weiter" gate treats species like ageYears (required, non-empty)
   * on that branch. Backend DTO's @ValidateIf enforces the same
   * on the wire (create-book-order.dto.ts).
   */
  readonly species: string;
}

/**
 * Reload-trap fix (Sebastian ruling 2026-10-09 09:38) — the three
 * block indices the Konfigurator uses: Personen = 1, Freitext = 2,
 * Titelwunsch = 3. Exported here so routes/tests can type-check
 * without importing from the route file.
 */
export type BlockIndex = 1 | 2 | 3;

/**
 * Reload-trap fix — persisted navigation state. `reached` is the set
 * of blocks the customer has advanced to (deduplicated); `openBlock`
 * is which block is currently expanded (or `null` when the terminal
 * "weiter zur Geschichte" CTA is showing). Modeled as a plain array
 * (not a Set) for JSON serialisability through the Zustand `persist`
 * middleware.
 *
 * `null` for the whole field means "not yet decided" — happens on a
 * first load, on a migrated draft from v2, and after `reset()`.
 * PersonenRoute's post-photo-hydration effect decides the first
 * value based on cast completeness + the shared `isPersonComplete`
 * helper, then writes it back here.
 */
export interface BlockNavState {
  readonly reached: readonly BlockIndex[];
  readonly openBlock: BlockIndex | null;
}

// ──────────────────────────────────────────────────────────────────
// PROTOTYPE – texts not approved
//
// Geschichtenschritt (feat/story-step-stations-prototype, Sebastian
// GO 2026-10-08). The guided surface shrank from the round-1
// stations+clothing flow to just three keys:
//
//   storyKind      — 'erlebt' | 'erfunden' | null, chosen on step 1
//   erfundenTile   — the erfunden-step-2 tile tap; null until picked
//   freitext       — still the single source of truth for the
//                    backend `description`
//
// Round-3c (Sebastian 2026-10-08 late evening) split `freitext` into
// two kind buckets `textErlebt` + `textErfunden`; `freitext` is now
// a VIEW that mirrors whichever bucket matches the current storyKind.
// `lastGeneratedText` (round-3b) carries the exact prefill string the
// store last inserted so the cast-change regen effect can detect
// edited vs unedited text byte-identically.
// ──────────────────────────────────────────────────────────────────

export type StoryKind = 'erlebt' | 'erfunden';
export type ErfundenTileSlug =
  // Round 3 (Sebastian click-mockup 2026-10-08 evening) — tile keys
  // match the mockup's `tileDefs()` identifiers. "eigene" replaces
  // round-2 "anderes"; "weltraum" replaces "weltall"; "superhelden"
  // replaces "superheld"; "dino" replaces "dinosaurier"; "unterwasser"
  // is new. Migrate at v5→v6 remaps round-2 slugs.
  | 'eigene'
  | 'feuerwehr'
  | 'weltraum'
  | 'piraten'
  | 'superhelden'
  | 'polizei'
  | 'zauberwald'
  | 'dino'
  | 'unterwasser';

export interface BookConfiguratorState {
  readonly cast: readonly CastTextEntry[];
  /**
   * Round 3c (Sebastian 2026-10-08 late evening) — the VIEW field
   * that the UI textarea binds to and the payload builder reads
   * from. Mirrors `textErlebt` or `textErfunden` depending on the
   * current `storyKind`; the two kind-buckets are the true sources
   * and ensure that user text from the OTHER kind never leaks into
   * the field after a kind switch. See `setFreitext` + `setStoryKind`
   * for the invariant.
   */
  readonly freitext: string;
  /** Round 3c — remembered text for the erlebt path. Empty by default. */
  readonly textErlebt: string;
  /** Round 3c — remembered text for the erfunden path. Empty by default. */
  readonly textErfunden: string;
  /** ISO 639-1. Default `'de'` per J-10; today only DE ships. */
  readonly sprache: string;
  /**
   * Block 2 commit 1 (Sebastian ruling 2026-08-31) — optional book-
   * level title wish. Empty string when the customer skips the step.
   */
  readonly titelwunsch: string;
  /**
   * Reload-trap fix (Sebastian ruling 2026-10-09 09:38) — persisted
   * `{reached, openBlock}` pair so a reload restores the customer to
   * the exact block they were on. `null` means "not yet decided" and
   * triggers the derive-fallback in PersonenRoute after photo
   * hydration finishes.
   */
  readonly blockNav: BlockNavState | null;

  // PROTOTYPE geschichtenschritt state (round 2) — auxiliary; the
  // round-1 bulk is retired. `freitext` stays the single source of
  // truth for the backend description.
  readonly storyKind: StoryKind | null;
  readonly erfundenTile: ErfundenTileSlug | null;
  /**
   * Round 3b (Sebastian 2026-10-08) — the exact string we last inserted
   * into `freitext` from a theme-tile prefill. Compared verbatim
   * against `freitext` to detect "unedited" state across cast changes
   * (which would otherwise alter the recomputed sentence and make the
   * stored field look user-edited). `null` when no sentence has been
   * generated or the field was cleared / tile was `eigene`.
   */
  readonly lastGeneratedText: string | null;

  // Cast actions
  readonly addCast: () => string;
  readonly updateCast: (
    key: string,
    patch: Partial<
      Pick<
        CastTextEntry,
        'name' | 'ageYears' | 'personType' | 'geschlecht' | 'personenNotiz' | 'species'
      >
    >,
  ) => void;
  readonly removeCast: (key: string) => void;

  // Text-block actions
  readonly setFreitext: (text: string) => void;
  readonly setSprache: (lang: string) => void;
  readonly setTitelwunsch: (text: string) => void;

  // Block-navigation action (reload-trap fix)
  readonly setBlockNav: (nav: BlockNavState) => void;

  // PROTOTYPE geschichtenschritt setters (round 2)
  readonly setStoryKind: (p: StoryKind | null) => void;
  readonly setErfundenTile: (t: ErfundenTileSlug | null) => void;
  readonly setLastGeneratedText: (t: string | null) => void;
  /**
   * Round 3c — kind-explicit setter used by the cast-change
   * regeneration effect in `GeschichtenschrittBlock`. Writes
   * `textErfunden` AND, if the current `storyKind` is `'erfunden'`,
   * also updates `freitext` so the visible field refreshes. Keeps
   * the two-text invariant intact when the current view is `'erlebt'`
   * — `textErfunden` can be refreshed in the background without
   * clobbering the erlebt view.
   */
  readonly setTextErfunden: (t: string) => void;

  readonly reset: () => void;
}

/** Cast key generator — short and unique enough for the 8-entry ceiling. */
function makeCastKey(): string {
  const bytes = new Uint8Array(6);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 6; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** The empty first cast entry seed — Block 2 removes the Hauptperson
 *  distinction; cast[0] is just the first entry with the default
 *  personType ('person').
 *
 *  Sebastian ruling 2026-09-19 — a fresh person entry starts with
 *  `geschlecht: ''` (unset). The segmented control renders with no
 *  button pressed and the customer must pick one of the three
 *  values before Save enables. When the customer flips the entry to
 *  Tier, `PersonenRoute.tsx` auto-fills `geschlecht: 'keineAngabe'`
 *  (Tier's pre-ruling default) so the Tier branch keeps its
 *  optional-with-default UX. */
function seedCast(): readonly CastTextEntry[] {
  return [
    {
      key: makeCastKey(),
      name: '',
      ageYears: null,
      personType: 'person',
      geschlecht: '',
      personenNotiz: '',
      species: '',
    },
  ];
}

/**
 * Reload-trap fix — the fresh-customer default. Block 1 open, nothing
 * else reached yet. Also what `reset()` returns to so a post-Light-
 * Gate landing starts clean at block 1. Migrated v2 drafts land at
 * `blockNav: null` instead, which triggers PersonenRoute's derive-
 * fallback (rule 3) after photo hydration.
 */
const FRESH_BLOCK_NAV: BlockNavState = { reached: [1], openBlock: 1 };

/**
 * W28 Option B (Sebastian 2026-10-12) — "does the signed-in user
 * have a local draft worth keeping?" predicate consumed by
 * `PersonenRoute` at mount time. When this returns true AND the user
 * also has a server-side `pendingBookOrderId`, the W28 redirect to
 * `/book/<pendingId>` is SKIPPED so the customer's in-progress local
 * work is not hijacked. The old server-side draft stays reachable
 * via `/book/meine`.
 *
 * "Has content" is defined narrowly on the TEXT state alone — the
 * configurator store is text-only by design (photos live in IDB).
 * Reading the photo IDB here would need an async hop, which the
 * mount effect can't synchronously await; the cast-name signal is a
 * conservative-enough proxy because every photo-pick path in the
 * flow runs through a `name` entry first. Trade-off: a customer who
 * uploaded photos but has not typed any name yet is NOT considered
 * to have content, and will still be W28-redirected. Documented
 * limitation.
 *
 * Any one of the following flips the predicate true:
 *   - any cast entry has a non-empty trimmed `name`
 *   - trimmed `textErlebt` is non-empty
 *   - trimmed `textErfunden` is non-empty
 *   - `storyKind !== null`   (customer tapped a path choice)
 *   - `erfundenTile !== null` (customer tapped a tile)
 *
 * `titelwunsch` is NOT counted — that step sits BEHIND the Light-
 * Gate on the configurator flow, so a signed-in user with ONLY
 * `titelwunsch` populated is in a very unusual state (would have
 * had to type it directly into the store), and treating it as
 * content would over-trigger the skip. If a future step surfaces
 * Titelwunsch pre-LightGate, add it here.
 *
 * `blockNav` is NOT counted — a stale blockNav from a previous
 * closed-tab session with no actual data in it should not block
 * the resume.
 *
 * Pure predicate, no side effects. Accepts the minimum state shape
 * so unit tests can call it directly with a plain object.
 */
export function localDraftHasContent(
  state: Pick<
    BookConfiguratorState,
    'cast' | 'textErlebt' | 'textErfunden' | 'storyKind' | 'erfundenTile'
  >,
): boolean {
  for (const entry of state.cast) {
    if (entry.name.trim().length > 0) return true;
    // Probe transfer seeds a cast entry with ageYears set but the
    // name intentionally empty (Sebastian ruling 2026-10-15: the
    // customer picks book-side names). A reload at /book right
    // after transfer must NOT drop the seed via the W28 Case-1
    // redirect, so a non-null ageYears also qualifies as "has
    // content". Also fires for the book-side case of a customer
    // who typed an age before typing a name — preserving a
    // half-filled draft on reload is the right UX in both
    // directions.
    if (entry.ageYears !== null) return true;
  }
  if (state.textErlebt.trim().length > 0) return true;
  if (state.textErfunden.trim().length > 0) return true;
  if (state.storyKind !== null) return true;
  if (state.erfundenTile !== null) return true;
  return false;
}

/**
 * React hook returning the persist-middleware hydration flag. True iff
 * the store has finished reading its persisted slice from localStorage.
 *
 * For Zustand v5 with the default sync `createJSONStorage(() =>
 * localStorage)`, hydration completes synchronously at module-import
 * time, so this returns `true` on the first render in the vast
 * majority of cases. The hook still subscribes to
 * `onFinishHydration` as a defensive belt — any future switch to an
 * async storage (IDB, remote), or a manual
 * `useBookConfiguratorStore.persist.rehydrate()` after the component
 * is mounted, will flip the value correctly.
 *
 * Returning `false` means "not yet decided; do nothing" — DO NOT
 * fire a mount redirect that depends on the configurator store
 * being authoritative while this is false.
 */
export function useBookConfiguratorHydrated(): boolean {
  const [hydrated, setHydrated] = useState<boolean>(() =>
    useBookConfiguratorStore.persist.hasHydrated(),
  );
  useEffect(() => {
    const unsub = useBookConfiguratorStore.persist.onFinishHydration(() => {
      setHydrated(true);
    });
    if (!hydrated && useBookConfiguratorStore.persist.hasHydrated()) {
      setHydrated(true);
    }
    return unsub;
  }, [hydrated]);
  return hydrated;
}

/**
 * Shared hook consumed by every mount redirect to the user's pending
 * server-side book order. Returns `{ ready, hasContent }`:
 *
 *   - `ready` — is the configurator store hydrated? Any mount
 *     redirect must gate on this before deciding. While `ready` is
 *     false the caller must do nothing — redirecting while the local
 *     draft is still being read from localStorage would hijack the
 *     customer away from in-progress work.
 *   - `hasContent` — once `ready` is true, is `localDraftHasContent`
 *     currently satisfied? Subscribes to the store slice, so a late
 *     cast-name typed on a slow device still flips this true before
 *     the effect fires.
 *
 * Pass the `hasContent` value into the mount redirect's effect deps
 * so the effect re-evaluates when the slice changes. The effect's
 * `return` branch when `!ready || hasContent` is the W28 Option-B
 * skip (Sebastian ruling 2026-10-12 + 2026-10-13 reinforcement).
 *
 * One shared helper so no mount-redirect path bypasses the guard.
 * Today's single call site is `PersonenRoute`'s Case-1 mount
 * redirect; any new redirect to `/book/${pendingBookOrderId}` from
 * mount scope MUST route through this hook rather than reading
 * `getState()` directly.
 */
export function useLocalDraftRedirectGuard(): { ready: boolean; hasContent: boolean } {
  const ready = useBookConfiguratorHydrated();
  const hasContent = useBookConfiguratorStore((s) =>
    localDraftHasContent({
      cast: s.cast,
      textErlebt: s.textErlebt,
      textErfunden: s.textErfunden,
      storyKind: s.storyKind,
      erfundenTile: s.erfundenTile,
    }),
  );
  return { ready, hasContent };
}

const INITIAL_STATE: Pick<
  BookConfiguratorState,
  | 'cast'
  | 'freitext'
  | 'textErlebt'
  | 'textErfunden'
  | 'sprache'
  | 'titelwunsch'
  | 'blockNav'
  | 'storyKind'
  | 'erfundenTile'
  | 'lastGeneratedText'
> = {
  cast: seedCast(),
  freitext: '',
  textErlebt: '',
  textErfunden: '',
  sprache: 'de',
  titelwunsch: '',
  blockNav: FRESH_BLOCK_NAV,
  storyKind: null,
  erfundenTile: null,
  lastGeneratedText: null,
};

/**
 * ═══ PERSISTED-STATE VERSIONING (Sebastian ruling 2026-08-31 evening) ═
 *
 * The zustand `persist` middleware writes the partialized state to
 * `localStorage['dreamable-book-configurator']`. The customer's
 * browser stores whatever shape shipped when they last opened the
 * page. When the shape evolves and a customer comes back mid-flow,
 * the OLD shape rehydrates and every downstream reader inherits
 * missing fields.
 *
 * Block 2 commit 1 evening (Sebastian ruling): a customer with a
 * pre-Block-2 `cast[0]` in localStorage had no `personType`,
 * `geschlecht`, or `personenNotiz` on that entry. The payload builder
 * then called `entry.personenNotiz.trim()` on `undefined` inside
 * `LightGateOverlay.runUploadAndCreate`, which threw synchronously
 * outside the try/catch that wraps `createOrder.submit`. The "Wir
 * legen dein Buch an" overlay hung forever.
 *
 * ═══ THE RULE (invariant, for every future change) ═══════════════════
 *
 * Any change to `CastTextEntry` (add field / rename / change semantic
 * meaning) MUST:
 *   1. Bump `CURRENT_PERSIST_VERSION` by 1.
 *   2. Extend `migrate` to bring older persisted state up to the new
 *      shape via a case that names the previous version explicitly.
 *   3. Add a regression test that rehydrates from the previous shape
 *      and asserts the current shape emerges.
 *
 * Persisted state is an INPUT FROM THE PAST, not a trusted shape.
 * Treat it like a wire payload from an untrusted client — validate,
 * default, upgrade.
 *
 * The default zustand behaviour without version + migrate is
 * shallow-merging the old cast array over the fresh seed, so missing
 * fields stay missing. That is why version is REQUIRED here, not
 * optional.
 *
 * ═══ MERGED CHAIN (Sebastian 2026-10-09, feat/story-step-golive) ═════
 *
 * This store carries BOTH the main-branch reload-trap-fix (v2 → v3,
 * adds `blockNav`) AND the prototype branch's geschichtenschritt
 * state (v3 → v4..v8 for storyKind + erfundenTile + lastGeneratedText
 * + textErlebt + textErfunden). The chain:
 *
 *   v2 → v3  reload-trap fix adds `blockNav: BlockNavState | null`
 *            (null triggers PersonenRoute derive-fallback).
 *   v3 → v4  Geschichtenschritt round-2 adds storyKind + erfundenTile
 *            (the first prototype keys, both default `null`), plus
 *            empty textErlebt/textErfunden/lastGeneratedText.
 *            Legacy `freitext` seeds into the storyKind-driven
 *            bucket; the null-storyKind fallback is `textErlebt`.
 *   v4 → v5  Geschichtenschritt round-2 part 2 — mechanical slot
 *            in the chain. Shape unchanged from v4; retained so the
 *            chain mirrors the prototype branch's internal history.
 *   v5 → v6  Geschichtenschritt round-3 slug remap on erfundenTile
 *            (anderes→eigene, weltall→weltraum, superheld→superhelden,
 *            dinosaurier→dino; others pass through; unterwasser is new
 *            and has no v5 predecessor; unknown strings land at null).
 *   v6 → v7  Geschichtenschritt round-3b adds `lastGeneratedText`
 *            (defaults to null on older drafts — null never equals
 *            a non-empty freitext, so the customer is never surprised).
 *   v7 → v8  Geschichtenschritt round-3c splits `freitext` into
 *            `textErlebt` + `textErfunden` kind buckets (seeded by
 *            the current `storyKind`; the view `freitext` continues
 *            to mirror the current bucket).
 *
 * Branches `v4 → v5`, `v5 → v6`, `v6 → v7`, `v7 → v8` are documented
 * for chain integrity but will never fire against real customer state —
 * no shipped build ever persisted at those intermediate versions. The
 * live migration paths are `v2 → v8` (main pre-hotfix drafts) and
 * `v3 → v8` (main post-hotfix drafts). Both are tested in the companion
 * spec.
 */
const CURRENT_PERSIST_VERSION = 8;

/** Round 3c helper — split a legacy single `freitext` into the two
 *  kind buckets based on the persisted (or null) storyKind. */
function splitFreitextByKind(
  freitext: string,
  kind: StoryKind | null,
): { textErlebt: string; textErfunden: string } {
  if (kind === 'erfunden') return { textErlebt: '', textErfunden: freitext };
  return { textErlebt: freitext, textErfunden: '' };
}

/** Fresh default slice for the prototype keys — used by every
 *  migration path so a rehydrated draft always carries the full
 *  current shape. Changing these defaults means another version bump. */
function emptyGeschichteSlice(): Pick<
  BookConfiguratorState,
  'storyKind' | 'erfundenTile' | 'lastGeneratedText' | 'textErlebt' | 'textErfunden'
> {
  return {
    storyKind: null,
    erfundenTile: null,
    lastGeneratedText: null,
    textErlebt: '',
    textErfunden: '',
  };
}

/** Narrow helper — accept only known StoryKind strings; else null. */
function coerceStoryKind(raw: unknown): StoryKind | null {
  return raw === 'erlebt' || raw === 'erfunden' ? raw : null;
}

/** Round-3 slug remap for v5 drafts whose erfundenTile still carries
 *  the pre-round-3 identifiers. Returns the canonical slug or null. */
function remapRound3Slug(raw: unknown): ErfundenTileSlug | null {
  if (typeof raw !== 'string') return null;
  switch (raw) {
    case 'anderes':
      return 'eigene';
    case 'weltall':
      return 'weltraum';
    case 'superheld':
      return 'superhelden';
    case 'dinosaurier':
      return 'dino';
    case 'eigene':
    case 'feuerwehr':
    case 'weltraum':
    case 'piraten':
    case 'superhelden':
    case 'polizei':
    case 'zauberwald':
    case 'dino':
    case 'unterwasser':
      return raw;
    default:
      return null;
  }
}

/**
 * Bring persisted state at `version` up to `CURRENT_PERSIST_VERSION`.
 * Runs BEFORE the persisted state is merged into the store. The
 * input `persistedState` is `unknown` because it comes from
 * localStorage — its shape at read time is whatever version was
 * written at last save time (0 = pre-versioning = pre-Block-2).
 *
 * Contract: never throws. On any shape it does not recognise, return
 * `INITIAL_STATE`'s partialized slice so the customer restarts clean
 * rather than crashes downstream.
 *
 * Each `if (version < N)` branch below produces the FULL v8 shape in
 * one hop — there is no in-function version walking. The chain-step
 * comments above describe what each hop semantically represents.
 */
function migratePersistedState(persistedState: unknown, version: number): unknown {
  if (persistedState === null || typeof persistedState !== 'object') {
    return {
      cast: seedCast(),
      freitext: '',
      sprache: 'de',
      titelwunsch: '',
      blockNav: null,
      ...emptyGeschichteSlice(),
    };
  }
  const state = persistedState as Record<string, unknown>;

  // v0 → v8 — pre-Block-2 shape. cast[i] may be missing personType,
  // geschlecht, personenNotiz; may still carry the deleted
  // isProtagonist. Defaults per Block 2 commit 1:
  //   personType = 'person'      (§Sebastian ruling 2026-08-31 — the
  //                              common case needs no interaction)
  //   geschlecht = 'keineAngabe' (§same — customer can skip)
  //   personenNotiz = ''         (empty is honest; customer types if
  //                              they want to)
  // v1 → v8 (Position-2 Schritt B, Sebastian ruling 2026-09-11) —
  //   species = ''               (empty on all rehydrated pre-v2
  //                              entries; customer types on the
  //                              Personen tier-branch input at
  //                              re-entry, and the Weiter gate keeps
  //                              him there until he does)
  // blockNav lands at null → PersonenRoute's derive-fallback runs
  // after photo hydration. Prototype keys empty; legacy freitext
  // seeded into textErlebt (null kind fallback).
  if (version < 2) {
    const rawCast = Array.isArray(state.cast) ? state.cast : [];
    const upgradedCast: CastTextEntry[] = rawCast.map((raw): CastTextEntry => {
      const entry = (raw ?? {}) as Record<string, unknown>;
      const rawPersonType = entry.personType;
      const rawGeschlecht = entry.geschlecht;
      const rawNotiz = entry.personenNotiz;
      const rawSpecies = entry.species;
      return {
        key: typeof entry.key === 'string' && entry.key.length > 0 ? entry.key : makeCastKey(),
        name: typeof entry.name === 'string' ? entry.name : '',
        ageYears: typeof entry.ageYears === 'number' ? entry.ageYears : null,
        personType:
          rawPersonType === 'person' || rawPersonType === 'tier' ? rawPersonType : 'person',
        geschlecht:
          rawGeschlecht === 'maennlich' ||
          rawGeschlecht === 'weiblich' ||
          rawGeschlecht === 'keineAngabe'
            ? rawGeschlecht
            : 'keineAngabe',
        personenNotiz: typeof rawNotiz === 'string' ? rawNotiz : '',
        species: typeof rawSpecies === 'string' ? rawSpecies : '',
        // isProtagonist (if present on the raw entry) is silently
        // dropped by not being copied into the returned shape.
      };
    });
    const freitext = typeof state.freitext === 'string' ? state.freitext : '';
    return {
      cast: upgradedCast.length > 0 ? upgradedCast : seedCast(),
      freitext,
      sprache: typeof state.sprache === 'string' ? state.sprache : 'de',
      titelwunsch: typeof state.titelwunsch === 'string' ? state.titelwunsch : '',
      blockNav: null,
      ...emptyGeschichteSlice(),
      ...splitFreitextByKind(freitext, null),
    };
  }

  // v2 → v8 — main's pre-hotfix shape (base keys only, no blockNav,
  // no prototype keys). blockNav lands at null; prototype keys land
  // empty; legacy freitext is seeded into textErlebt (null kind
  // fallback). Base keys pass through byte-identical.
  if (version < 3) {
    const freitext = typeof state.freitext === 'string' ? state.freitext : '';
    return {
      cast: Array.isArray(state.cast) && state.cast.length > 0 ? state.cast : seedCast(),
      freitext,
      sprache: typeof state.sprache === 'string' ? state.sprache : 'de',
      titelwunsch: typeof state.titelwunsch === 'string' ? state.titelwunsch : '',
      blockNav: null,
      ...emptyGeschichteSlice(),
      ...splitFreitextByKind(freitext, null),
    };
  }

  // v3 → v8 — main's post-hotfix shape (base keys + blockNav; no
  // prototype keys). blockNav passes through verbatim; prototype keys
  // land empty; legacy freitext is seeded into textErlebt (null kind
  // fallback). This is the main real-customer upgrade path on this
  // branch — ships the moment the carry-over lands on main.
  if (version < 4) {
    const freitext = typeof state.freitext === 'string' ? state.freitext : '';
    return {
      cast: Array.isArray(state.cast) && state.cast.length > 0 ? state.cast : seedCast(),
      freitext,
      sprache: typeof state.sprache === 'string' ? state.sprache : 'de',
      titelwunsch: typeof state.titelwunsch === 'string' ? state.titelwunsch : '',
      blockNav: isBlockNav(state.blockNav) ? state.blockNav : null,
      ...emptyGeschichteSlice(),
      ...splitFreitextByKind(freitext, null),
    };
  }

  // v4 → v8 — chain-documentation slot (prototype round-2 half-way
  // state; never shipped). Trusts storyKind if coercible. Legacy
  // freitext seeds into the kind-matching bucket; the other bucket
  // starts empty.
  if (version < 5) {
    const storyKind = coerceStoryKind(state.storyKind);
    const freitext = typeof state.freitext === 'string' ? state.freitext : '';
    return {
      cast: Array.isArray(state.cast) && state.cast.length > 0 ? state.cast : seedCast(),
      freitext,
      sprache: typeof state.sprache === 'string' ? state.sprache : 'de',
      titelwunsch: typeof state.titelwunsch === 'string' ? state.titelwunsch : '',
      blockNav: isBlockNav(state.blockNav) ? state.blockNav : null,
      storyKind,
      erfundenTile: remapRound3Slug(state.erfundenTile),
      lastGeneratedText: null,
      ...splitFreitextByKind(freitext, storyKind),
    };
  }

  // v5 → v8 — chain-documentation slot (round-3 slug remap; never
  // shipped). Same as v4→v8 shape: slug remap applies to any raw
  // erfundenTile; everything else as before.
  if (version < 6) {
    const storyKind = coerceStoryKind(state.storyKind);
    const freitext = typeof state.freitext === 'string' ? state.freitext : '';
    return {
      cast: Array.isArray(state.cast) && state.cast.length > 0 ? state.cast : seedCast(),
      freitext,
      sprache: typeof state.sprache === 'string' ? state.sprache : 'de',
      titelwunsch: typeof state.titelwunsch === 'string' ? state.titelwunsch : '',
      blockNav: isBlockNav(state.blockNav) ? state.blockNav : null,
      storyKind,
      erfundenTile: remapRound3Slug(state.erfundenTile),
      lastGeneratedText: null,
      ...splitFreitextByKind(freitext, storyKind),
    };
  }

  // v6 → v8 — chain-documentation slot (round-3b lastGeneratedText;
  // never shipped). Trusts the raw erfundenTile if it's already a
  // round-3 slug (round-3 remap already happened at v5→v6); older
  // drafts never reach this branch because v4 and v5 branches above
  // short-circuit.
  if (version < 7) {
    const storyKind = coerceStoryKind(state.storyKind);
    const freitext = typeof state.freitext === 'string' ? state.freitext : '';
    return {
      cast: Array.isArray(state.cast) && state.cast.length > 0 ? state.cast : seedCast(),
      freitext,
      sprache: typeof state.sprache === 'string' ? state.sprache : 'de',
      titelwunsch: typeof state.titelwunsch === 'string' ? state.titelwunsch : '',
      blockNav: isBlockNav(state.blockNav) ? state.blockNav : null,
      storyKind,
      erfundenTile: remapRound3Slug(state.erfundenTile),
      lastGeneratedText: null,
      ...splitFreitextByKind(freitext, storyKind),
    };
  }

  // v7 → v8 — chain-documentation slot (round-3c kind buckets added;
  // never shipped). Trusts persisted lastGeneratedText; seeds the two
  // buckets from the legacy single freitext.
  if (version < 8) {
    const storyKind = coerceStoryKind(state.storyKind);
    const freitext = typeof state.freitext === 'string' ? state.freitext : '';
    const lastGeneratedText =
      typeof state.lastGeneratedText === 'string' || state.lastGeneratedText === null
        ? (state.lastGeneratedText as string | null)
        : null;
    return {
      cast: Array.isArray(state.cast) && state.cast.length > 0 ? state.cast : seedCast(),
      freitext,
      sprache: typeof state.sprache === 'string' ? state.sprache : 'de',
      titelwunsch: typeof state.titelwunsch === 'string' ? state.titelwunsch : '',
      blockNav: isBlockNav(state.blockNav) ? state.blockNav : null,
      storyKind,
      erfundenTile: remapRound3Slug(state.erfundenTile),
      lastGeneratedText,
      ...splitFreitextByKind(freitext, storyKind),
    };
  }

  // version === CURRENT_PERSIST_VERSION — nothing to migrate, pass
  // through. Any FUTURE version bump extends this file with another
  // `if (version < N)` block that mirrors the shape change and adds a
  // regression test. See the JSDoc block above for the rule.
  return persistedState;
}

/** Narrow guard — accept only well-shaped BlockNavState rows. */
function isBlockNav(raw: unknown): raw is BlockNavState {
  if (raw === null || typeof raw !== 'object') return false;
  const nav = raw as Record<string, unknown>;
  if (!Array.isArray(nav.reached)) return false;
  for (const r of nav.reached) {
    if (r !== 1 && r !== 2 && r !== 3) return false;
  }
  const open = nav.openBlock;
  if (open !== null && open !== 1 && open !== 2 && open !== 3) return false;
  return true;
}

/**
 * Exported for testing only. Rehydrating a pre-v1 blob through this
 * function should produce the current v8 shape, and rehydrating a
 * v8 blob should return it unchanged.
 */
export const __migratePersistedState = migratePersistedState;

export const useBookConfiguratorStore = create<BookConfiguratorState>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      addCast: () => {
        const key = makeCastKey();
        set((state) => ({
          cast: [
            ...state.cast,
            {
              key,
              name: '',
              ageYears: null,
              personType: 'person',
              // Sebastian ruling 2026-09-19 — fresh entries start
              // unset. Person entries stay unset until the customer
              // picks one of the three (Save is gated on this);
              // Tier entries auto-flip to `'keineAngabe'` in
              // `PersonenRoute.tsx::onUpdatePersonType` so the Tier
              // branch keeps its optional-with-default UX.
              geschlecht: '',
              personenNotiz: '',
              species: '',
            },
          ],
        }));
        return key;
      },

      updateCast: (key, patch) => {
        set((state) => ({
          cast: state.cast.map((entry) => (entry.key === key ? { ...entry, ...patch } : entry)),
        }));
      },

      removeCast: (key) => {
        // Sebastian ruling 2026-09-24 — cast order has no technical
        // meaning (verified: story language derives from max(ageYears)
        // in `protagonist-year-derivation.service.ts:129-148`, not
        // from cast[0]). The old first-entry-protected guard is
        // dropped so the one-question-at-a-time flow can offer
        // "Entfernen" on every collapsed card, including the first.
        // If cast becomes empty, the flow starts again at Q1.
        set((state) => ({
          cast: state.cast.filter((entry) => entry.key !== key),
        }));
      },

      /**
       * Round 3c — `freitext` is the VIEW over the two kind buckets.
       * Writing routes to the bucket of the current kind so that a
       * subsequent kind switch (via setStoryKind) can restore the
       * other kind's text exactly.
       *
       * Edge case — `storyKind === null`: no kind has been chosen yet.
       * The textarea is not even mounted in this state, so writes only
       * reach this setter via migration-era fallbacks and tests. We
       * route to `textErlebt` (the "default" bucket) AND `freitext`,
       * so that whichever path the customer later picks, text is never
       * dropped. Documented here and asserted by store tests.
       */
      setFreitext: (text) =>
        set((state) => {
          if (state.storyKind === 'erfunden') {
            return { freitext: text, textErfunden: text };
          }
          return { freitext: text, textErlebt: text };
        }),

      setSprache: (lang) => set({ sprache: lang }),
      setTitelwunsch: (text) => set({ titelwunsch: text }),

      setBlockNav: (nav) => set({ blockNav: nav }),

      /**
       * Round 3c — on a real kind change, hydrate `freitext` from the
       * new kind's bucket so the view reflects the other kind's
       * remembered text exactly (or '' if that side was never
       * written). `null` only ever lands via `reset()` or admin paths;
       * we keep `freitext` as-is on null transitions so no text
       * silently disappears from the view.
       */
      setStoryKind: (p) =>
        set((state) => {
          if (p === null) return { storyKind: null };
          const next = p === 'erfunden' ? state.textErfunden : state.textErlebt;
          return { storyKind: p, freitext: next };
        }),
      setErfundenTile: (t) => set({ erfundenTile: t }),
      // Round 3b setter
      setLastGeneratedText: (t) => set({ lastGeneratedText: t }),
      /**
       * Round 3c — kind-explicit setter used by the cast-change
       * regeneration effect. Updates `textErfunden` unconditionally;
       * ALSO updates `freitext` iff the current `storyKind === 'erfunden'`
       * so the visible field (and therefore the payload) refreshes
       * together with the bucket. When the current view is `erlebt`,
       * only the bucket is updated — regenerating in the background
       * must not disturb the erlebt view.
       */
      setTextErfunden: (t) =>
        set((state) => {
          if (state.storyKind === 'erfunden') {
            return { textErfunden: t, freitext: t };
          }
          return { textErfunden: t };
        }),

      reset: () => set(INITIAL_STATE),
    }),
    {
      name: 'dreamable-book-configurator',
      version: CURRENT_PERSIST_VERSION,
      migrate: migratePersistedState,
      // Persist only text state. Photos are transient by design (see
      // JSDoc header). Actions are functions and don't serialise.
      // Both the reload-trap `blockNav` and the Geschichtenschritt
      // prototype keys persist alongside the base keys so a reload
      // mid-flow keeps the customer on their current block and (if
      // they already answered step 1/2 of the story step) on their
      // current sub-screen.
      partialize: (state) => ({
        cast: state.cast,
        freitext: state.freitext,
        textErlebt: state.textErlebt,
        textErfunden: state.textErfunden,
        sprache: state.sprache,
        titelwunsch: state.titelwunsch,
        blockNav: state.blockNav,
        storyKind: state.storyKind,
        erfundenTile: state.erfundenTile,
        lastGeneratedText: state.lastGeneratedText,
      }),
    },
  ),
);
